import React from "react";
import {RouteComponentProps} from "react-router-dom";
import {BrandAdminServiceSingleton} from "src/Context";
import {AdminBrand} from "../model/BrandTypes";
import "../Branding.scss";

interface State {
    brands: AdminBrand[];
    loading: boolean;
    error: string | null;
    /** Slug of the brand whose link was just copied, for the inline confirmation. */
    copied: string | null;
}

export class BrandListPage extends React.Component<RouteComponentProps, State> {

    state: State = {brands: [], loading: true, error: null, copied: null};

    componentDidMount() {
        this.load();
    }

    private load = () => {
        this.setState({loading: true, error: null});
        BrandAdminServiceSingleton.list()
            .then(brands => this.setState({brands, loading: false}))
            .catch(e => this.setState({error: this.message(e), loading: false}));
    };

    private message = (e: any) => (e && e.message) ? e.message : "Could not load brands";

    /** The borrower-facing link for a brand: its own path, e.g. `pay.nflp.com/vista`. */
    private paymentLink = (brand: AdminBrand): string => {
        // The payment pages are served from the same origin as this dashboard, both on the dev
        // server and when deployed. Using the API host instead would produce links to the wrong
        // service wherever the two are split.
        //
        // Deployed, the backend answers a brand's path with the payment page (BrandPageFilter).
        // The CRA dev server mounts the app under PUBLIC_URL only, so there the link keeps that
        // prefix — /payment-app/vista — and the page reads the brand past it.
        //
        // Every link names its brand, the default's included. A plain link means "whatever is the
        // default right now", so handing one out for the default brand would quietly change
        // which brand it opens the next time another brand is made the default.
        const prefix = process.env.NODE_ENV === "development" ? (process.env.PUBLIC_URL || "") : "";
        return `${window.location.origin}${prefix}/${encodeURIComponent(brand.slug)}`;
    };

    private copyLink = (brand: AdminBrand) => {
        const link = this.paymentLink(brand);
        const done = () => {
            this.setState({copied: brand.slug});
            window.setTimeout(() => this.setState(prev =>
                prev.copied === brand.slug ? {copied: null} as State : null as any), 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link).then(done).catch(() => this.fallbackCopy(link, done));
        } else {
            this.fallbackCopy(link, done);
        }
    };

    /** clipboard.writeText needs a secure context, which plain-http local testing is not. */
    private fallbackCopy = (text: string, done: () => void) => {
        const area = document.createElement("textarea");
        area.value = text;
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        try { document.execCommand("copy"); done(); } finally { document.body.removeChild(area); }
    };

    private makeDefault = (brand: AdminBrand) => {
        BrandAdminServiceSingleton.makeDefault(brand.id).then(this.load)
            .catch(e => this.setState({error: this.message(e)}));
    };

    private toggleActive = (brand: AdminBrand) => {
        BrandAdminServiceSingleton.setActive(brand.id, !brand.active).then(this.load)
            .catch(e => this.setState({error: this.message(e)}));
    };

    render() {
        const {brands, loading, error} = this.state;
        return (
            <div className="branding">
                <div className="branding__bar">
                    <div>
                        <h2 className="branding__title">Brands</h2>
                        <p className="branding__subtitle">
                            Each brand is selected by its slug on the borrower&rsquo;s payment link.
                        </p>
                    </div>
                    <button className="branding__btn branding__btn--primary"
                            onClick={() => this.props.history.push("/internal/branding/new")}>
                        NEW BRAND
                    </button>
                </div>

                {error ? <div className="branding__error">{error}</div> : null}
                {loading ? <div className="branding__empty">Loading&hellip;</div> : null}

                {!loading && brands.length === 0
                    ? <div className="branding__empty">No brands yet.</div>
                    : null}

                {brands.map(brand => (
                    <div className={"brand-card" + (brand.active ? "" : " brand-card--inactive")}
                         key={brand.id}>
                        <span className="brand-card__swatch"
                              style={{background: brand.theme.surface}}
                              title={brand.primaryColor}/>
                        <div className="brand-card__main">
                            <div className="brand-card__name">
                                {brand.displayName}
                                {brand.isDefault ? <span className="brand-card__pill">DEFAULT</span> : null}
                                {!brand.active ? <span className="brand-card__pill brand-card__pill--muted">INACTIVE</span> : null}
                            </div>
                            <div className="brand-card__slug">{this.paymentLink(brand)}</div>
                            {!brand.theme.bodyTextOnSurfacePasses
                                ? <div className="brand-card__warn">
                                    Body text on this color is {brand.theme.onSurfaceContrast}:1 — below 4.5:1
                                  </div>
                                : null}
                        </div>
                        <div className="brand-card__actions">
                            <a className="branding__btn" href={this.paymentLink(brand)}
                               target="_blank" rel="noopener noreferrer">
                                OPEN
                            </a>
                            <button className="branding__btn" onClick={() => this.copyLink(brand)}>
                                {this.state.copied === brand.slug ? "COPIED" : "COPY LINK"}
                            </button>
                            <button className="branding__btn"
                                    onClick={() => this.props.history.push("/internal/branding/" + brand.id)}>
                                EDIT
                            </button>
                            {!brand.isDefault
                                ? <button className="branding__btn" onClick={() => this.makeDefault(brand)}>
                                    MAKE DEFAULT
                                  </button>
                                : null}
                            {/* The default brand serves every link without a slug, so it must stay active. */}
                            {!brand.isDefault
                                ? <button className="branding__btn" onClick={() => this.toggleActive(brand)}>
                                    {brand.active ? "DEACTIVATE" : "ACTIVATE"}
                                  </button>
                                : null}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
}
